import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { DialogRef } from '@fundamental-ngx/core/dialog';
import { NotificationsService } from '../../../../service/services/misc/notifications/notifications.service';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { BaseCreateDialogComponent } from '../../../../core/components/abstract/base-create-dialog/base-create-dialog.component';
import { OrganizationModel } from '../../../../core/models/resources/organization.model';
import { OrganizationService } from '../../../../service/services/rest/organization/organization.service';
import { CostObjectModel } from '../../../../core/models/misc/costObject.model';
import { PopoverComponent } from '@fundamental-ngx/core/popover';
import { Subscription } from 'rxjs';
import { CostObjectService } from '../../../../service/services/rest/costObject/costObject.service';
import { UniqueLabels } from '../../../../core/helpers/Const';
import { ResourceModel } from '../../../../core/models/resources/resource.model';
import { Utils } from '../../../../core/helpers/Utils';
import { CustomValidators } from '../../../../core/helpers/CustomValidators';
import { SideDialogComponent } from '../../../../shared/components/side-dialog/side-dialog.component';
import { SwaService } from '../../../../service/services/misc/swa/swa.service';
import {DialogService} from "../../../../service/services/misc/dialog/dialog.service";
import {UisService} from "../../../../service/services/misc/uis/uis.service";

@Component({
    selector: 'app-create-organization-side-dialog',
    templateUrl: './create-organization-dialog.component.html',
    styleUrls: ['./create-organization-dialog.component.scss'],
    standalone: false
})
export class CreateOrganizationDialogComponent extends BaseCreateDialogComponent implements OnInit {
	@ViewChild('typeahead')
	    typeahead: PopoverComponent;

	@ViewChild('dialog') private dialog: SideDialogComponent;

	public costObjectOptions: CostObjectModel[];
	public costObjectsData: CostObjectModel[] = [];
	public costObjectIds: string[];
	public subscriptions: Subscription[] = [];

	files: File[];
	public jsonExample = {
	    displayName: 'My Organization',
	    name: 'my-organization',
	    description: 'This is my new organization',
	};
	public yamlExample = `displayName: "My Organization" <br>name: my-organization <br>description: "This is my new organization" <br>`;

	constructor(
		public override dialogRef: DialogRef,
    public override dialogService: DialogService,
		protected override notificationsService: NotificationsService,
		protected override fb: UntypedFormBuilder,
		protected override cdRef: ChangeDetectorRef,
		private organizationService: OrganizationService,
		private costObjectService: CostObjectService,
		protected override _swaService: SwaService,
		protected override _uisService: UisService
	) {
	    super(dialogRef, dialogService, notificationsService, fb, cdRef, _swaService, _uisService);
	    this.form.addControl('displayName', new UntypedFormControl('', Validators.required));
	}

	override ngOnInit() {
	    super.ngOnInit();
	    if (this.isEdit) {
	        let costObject = this.dialogRef.data.resource.costObject;
	        let isAgreementChecked = !!costObject;

	        this.form.addControl('costObject', new UntypedFormControl(costObject));
	        this.form.addControl(
	            'costObjectAgreement',
	            new UntypedFormControl(
	                { value: isAgreementChecked, disabled: !isAgreementChecked },
	                isAgreementChecked ? Validators.requiredTrue : []
	            )
	        );

	        this.costObjectOptions = [];

	        this.subscriptions.push(
	            this.costObjectService.getCostObjects().subscribe((res: CostObjectModel[]) => {
	                this.costObjectsData = res;
	                this.form.controls['costObject'].setValidators([CustomValidators.ValidateCostObject(res)]);
	            })
	        );
	    }
	}

	filterCostObjects(value: string): CostObjectModel[] {
	    const filterValue = value.toLowerCase();
	    if (filterValue.length === 0) {
	        return [];
	    }
	    return this.costObjectsData.filter(
	        (item) =>
	            item.COST_OBJECT_ID.includes(filterValue) ||
				(item.PERSON_RESP_MAIL && item.PERSON_RESP_MAIL.toLowerCase().includes(filterValue)) ||
				item.DESCRIPTION.includes(filterValue)
	    );
	}

	onCostObjectSearchChange(): void {
	    let costObject = this.form.value.costObject;
	    this.adjustCostAgreementState(costObject);
	    this.costObjectOptions = this.filterCostObjects(this.form.value.costObject).slice(0, 10);
	    this.refreshPosition();
	}

	adjustCostAgreementState(costObject: string) {
	    let checkbox = this.form.get('costObjectAgreement');
	    if (costObject) {
	        checkbox.setValidators([Validators.requiredTrue]);
	        checkbox.enable();
	    } else {
	        checkbox.setValidators([]);
	        checkbox.disable();
	    }
	    checkbox.updateValueAndValidity();
	}

	onCostObjectItemClick(clickedValue: CostObjectModel): void {
	    this.form.get('costObject')?.setValue(clickedValue.COST_OBJECT_ID);
	    this.costObjectOptions = [];
	}

	refreshPosition(): void {
	    this.typeahead.refreshPosition();
	}

	handleFileSelection(files: File[]): void {
	    Utils.parseYamlFile(files[0], (jsonRes: ResourceModel) => {
	        this.fillResourceFields(jsonRes);
	    });
	}

	fillResourceFields(jsonObj: ResourceModel) {
	    this.form.setValue({ displayName: jsonObj.displayName, name: jsonObj.name, description: jsonObj.description });
	}

	override onSubmit() {
	    this.sendEvent('Organization', this.form.getRawValue().name, this.isEdit);
	    this.setLoading(true);
	    let resource = new OrganizationModel({
	        name: this.form.getRawValue().name,
	        displayName: this.form.getRawValue().displayName,
	        description: this.form.getRawValue().description,
	        path: '',
	        labels: Utils.convertLabels(this.labels),
	        annotations: Utils.convertLabels(this.annotations),
	    });
	    if (this.form.value.costObject) {
	        resource.labels[UniqueLabels.COST_OBJECT_KEY] = this.form.value.costObject;
	    }
	    let resourceMethod = this.isEdit
	        ? this.organizationService.update(resource.path, resource.name, resource)
	        : this.organizationService.add(resource.path, resource.name, resource);
	    return resourceMethod
	        .subscribe({
	            next: this.onSuccess.bind(this),
	            error: this.onError.bind(this, this.dialog)
	        })
	        .add(() => {
	            this.setLoading(false);
	        });
	}
}
